import * as React from 'react';
import { runInAction } from 'mobx';

import TypedTransComponent from 'shared/components/TypedTransComponent';
import Typography from 'shared/newComponents/Typography';
import {
  TypographyColor,
  TypographySize,
  TypographyType
} from 'shared/newEntities/components/Typography';
import { IRootStore } from 'shared/entities/store/rootStore';
import { apiUrls } from 'shared/entities/domain';
import {
  AIAggregatedDocModelParams,
  AIAggregatedDocType,
  AIDocError,
  AIDocErrorCode,
  AIDocServer,
  AIDocsServer,
  AIUploadDocServerError,
  AIUploadDocServerResponse
} from 'shared/entities/smartbotAI';
import { AppNotificationType } from 'shared/entities/appNotifications';
import { LoadingStage } from 'shared/entities/meta';
import {
  addProtocolToUrl,
  getDomainNameFromUrl
} from 'shared/entities/common/utils';
import { SmoothedUploadProgress } from 'shared/models/file';

import { OpenStateModel } from '../openState';
import ListModel from '../ListModel';
import { LoadingStageModel } from '../loadingStage';
import { FieldModel } from '../form';

import { AIAggregatedDocModel } from './AIAggregatedDocModel';
import { AICommonSiteDocModel } from './AICommonSiteDocModel';
import {
  isFriendlySiteScanError,
  parseSiteIndexError,
  SITE_SCAN_FRIENDLY_ERROR_CODES
} from './utils';

const DOCS_LIMIT = 15;
const UPLOADING_PART_PROGRESS = 20;
const INDEXING_PART_PROGRESS = 100 - UPLOADING_PART_PROGRESS;

export class AIAggregatedSiteDocModel extends AIAggregatedDocModel<AIAggregatedDocType.site> {
  readonly docsModalState = new OpenStateModel();
  docs: ListModel<AICommonSiteDocModel> = new ListModel<AICommonSiteDocModel>();
  readonly docsLoadingStage = new LoadingStageModel();
  readonly searchValue: FieldModel<string> = new FieldModel<string>('');

  constructor(
    params: Omit<AIAggregatedDocModelParams<AIAggregatedDocType.site>, 'type'>
  ) {
    super({ ...params, type: AIAggregatedDocType.site });
  }
  //до 20 - это прогресс загрузки  сайта,  20-100 прогресс рассчитывается 20 + прогресс, приходящий из запроса poll * 0.8
  get progressView(): number {
    if (this.loadingStage.isLoading) {
      return this.loadingProgress.value;
    }
    return this.progress.value === 100
      ? this.progress.value
      : UPLOADING_PART_PROGRESS +
          Math.floor((this.progress.value * INDEXING_PART_PROGRESS) / 100);
  }

  getDocs = async (): Promise<BaseResponse> => {
    if (this.docsLoadingStage.isLoading) {
      return {
        isError: true
      };
    }

    this.docsLoadingStage.loading();

    const response = await this.rootStore.networkStore.api<AIDocsServer>(
      apiUrls.AI_LIST_DOCS,
      {
        method: 'GET',
        data: {
          ai_bot_id: this.botId,
          agg_key: this.id.value,
          offset: this.docs.length,
          limit: DOCS_LIMIT
        }
      }
    );

    if (response.isError) {
      runInAction(() => {
        this.docsLoadingStage.error();
        this.docs.setHasMore(false);
      });
      return {
        isError: true
      };
    }

    const collection: {
      keys: string[];
      entities: Record<string, AICommonSiteDocModel>;
    } = response.data.docs.reduce(
      (
        acc: { keys: string[]; entities: Record<string, AICommonSiteDocModel> },
        doc
      ) => {
        const model = AICommonSiteDocModel.fromJson({
          raw: doc,
          botId: this.botId,
          rootStore: this.rootStore
        });
        acc.keys.push(model.renderId);
        acc.entities[model.renderId] = model;
        return acc;
      },
      {
        entities: {},
        keys: []
      }
    );

    collection.keys.sort((keyA, keyB) => {
      const dateA = collection.entities[keyA].dateCreated.getTime();
      const dateB = collection.entities[keyB].dateCreated.getTime();
      return dateB - dateA;
    });

    runInAction(() => {
      this.docsLoadingStage.success();
      this.docs.addEntities({ ...collection, initial: false });
      this.docs.setHasMore(collection.keys.length === DOCS_LIMIT);
    });

    return {
      isError: false
    };
  };

  upload = async ({ url }: { url: string }): Promise<BaseResponse> => {
    if (this.loadingStage.isLoading) {
      return {
        isError: true
      };
    }
    this.loadingStage.loading();
    const urlWithProtocol = addProtocolToUrl(url, 'https');

    const uploadProgressSmooth = new SmoothedUploadProgress(
      () => this.loadingProgress.value,
      (v) => this.loadingProgress.changeValue(v)
    );
    uploadProgressSmooth.start();

    let response:
      | Awaited<
          ReturnType<
            typeof this.rootStore.networkStore.api<
              AIUploadDocServerResponse,
              AIDocErrorCode,
              AIUploadDocServerError
            >
          >
        >
      | undefined;
    try {
      response = await this.rootStore.networkStore.api<
        AIUploadDocServerResponse,
        AIDocErrorCode,
        AIUploadDocServerError
      >(apiUrls.AI_INDEX_SITE, {
        method: 'POST',
        data: {
          ai_bot_id: this.botId,
          url: urlWithProtocol,
          deep: false
        },
        showExpectedError: false,
        showUnexpectedError: true,
        expectedErrorCodes: SITE_SCAN_FRIENDLY_ERROR_CODES,
        config: {
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total === undefined || progressEvent.total <= 0) {
              return;
            }
            const loadedPct = Math.min(
              100,
              Math.floor((progressEvent.loaded / progressEvent.total) * 100)
            );
            uploadProgressSmooth.reportNetworkProgress(
              Math.floor((loadedPct * UPLOADING_PART_PROGRESS) / 100)
            );
          }
        }
      });
    } finally {
      uploadProgressSmooth.dispose();
    }

    if (!response) {
      this.loadingStage.error();
      this.rootStore.networkStore.showUnexpectedError(
        null,
        apiUrls.AI_INDEX_SITE
      );
      return {
        isError: true
      };
    }

    if (response.isError) {
      const errorPayload = parseSiteIndexError(response.data);
      const errors: AIDocError[] | null = errorPayload.code
        ? [
            {
              code: errorPayload.code as AIDocErrorCode,
              text: errorPayload.text || ''
            }
          ]
        : null;

      this.errors.changeValue(errors);
      this.loadingStage.error();
      if (isFriendlySiteScanError(errorPayload.code)) {
        this.showScanErrorNotification();
      }
      return {
        isError: true
      };
    }

    this.loadingProgress.changeValue(UPLOADING_PART_PROGRESS);
    this.id.changeValue(response.data.agg_key);
    this.loadingStage.success();
    return {
      isError: false
    };
  };

  showScanErrorNotification = (): void => {
    this.rootStore.appNotificationsStore.open({
      type: AppNotificationType.error,
      title: (
        <TypedTransComponent
          ns="smartbotAIPage"
          i18nKey="education.left.docs.site.scanErrorToast"
        >
          Не удалось обкачать сайт. Проверьте корректность ссылки и доступность
          ресурса. Если ошибка сохраняется, обратитесь в{' '}
          <Typography
            Element="span"
            fontType={TypographyType.text}
            fontSize={TypographySize.s}
            fontColor={TypographyColor.link}
            onClick={this.rootStore.supportPopupStore.openChat}
          >
            тех. поддержку
          </Typography>
          .
        </TypedTransComponent>
      )
    });
  };

  closeDocsModal = () => {
    this.docsModalState.close();
    this.docs.reset();
    this.searchValue.changeValue('');
  };

  removeDoc = async (renderId: string): Promise<BaseResponse> => {
    const doc = this.docs.getEntity(renderId);
    if (!doc) {
      return { isError: true };
    }
    const response = await doc.remove();
    if (response.isError) {
      return {
        isError: true
      };
    }

    this.docs.removeEntity(renderId);
    return { isError: false };
  };

  static fromDefaultParams({
    url,
    botId,
    rootStore
  }: {
    url: string;
    botId: string;
    rootStore: IRootStore;
  }): AIAggregatedSiteDocModel {
    const model = new AIAggregatedSiteDocModel({
      rootStore,
      id: null,
      botId,
      title: getDomainNameFromUrl(url) || url,
      url: null,
      loadingStage: LoadingStage.NOT_STARTED,
      cost: 1,
      errors: null,
      progress: 0,
      loadingProgress: 0,
      dateCreated: new Date()
    });

    return model;
  }

  static fromJson({
    raw,
    botId,
    rootStore
  }: {
    raw: AIDocServer;
    botId: string;
    rootStore: IRootStore;
  }): AIAggregatedSiteDocModel {
    return new AIAggregatedSiteDocModel({
      rootStore,
      id: raw.id,
      botId,
      title: raw.id,
      url: raw.url,
      loadingStage: LoadingStage.SUCCESS,
      cost: raw.cost,
      errors: raw.errors || null,
      progress: raw.progress,
      loadingProgress: UPLOADING_PART_PROGRESS,
      dateCreated: new Date(raw.date_created)
    });
  }
}
